/*
 * Copyright 2026 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/kmp-project-template/blob/main/LICENSE
 */
package kpt.core.designsystem.theme

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.Immutable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Shared elevation tier scale — five named tiers matching Material 3 elevation guidance.
 *
 * Access from any Composable via [MaterialTheme.elevation]. Sub-plan 02's [androidx.compose.material3.Card]
 * variants pick tiers from this scale rather than hardcoded `.dp` values.
 *
 * | Tier | Dp | Use case |
 * |------|----|----------|
 * | [resting] | 0 | Surface-level cards, filled cards, tonal cards |
 * | [low] | 1 | Standard elevated cards (M3 default) |
 * | [medium] | 3 | Hover state, pressed state, highlighted card |
 * | [high] | 6 | Floating action button, snackbar |
 * | [dragging] | 12 | Drag-and-drop visual state |
 */
@Immutable
data class Elevation(
    /** 0.dp — a surface-level, filled or tonal card. Material 3 conveys this with colour rather than shadow. */
    val resting: Dp = 0.dp,
    /** 1.dp — the default for an elevated card. */
    val low: Dp = 1.dp,
    /** 3.dp — hovered, pressed or otherwise highlighted. */
    val medium: Dp = 3.dp,
    /** 6.dp — a FAB or a snackbar, above ordinary content. */
    val high: Dp = 6.dp,
    /** 12.dp — an item held during drag-and-drop, which must read as lifted off the page. */
    val dragging: Dp = 12.dp,
)

/**
 * CompositionLocal carrying the app's elevation scale. `static` because it changes only with the
 * theme, so a read does not subscribe.
 */
val LocalElevation = staticCompositionLocalOf { Elevation() }

/** Resolve the active [Elevation] tier scale from composition. */
val MaterialTheme.elevation: Elevation
    @Composable
    @ReadOnlyComposable
    get() = LocalElevation.current
